import { request } from "../../utils/request";

/**
 * 获取剧集列表（首页剧集列表页）
 * 后端接口：/admin-api/video/series/page
 * 查询参数：pageNo, pageSize
 */
export const getSeriesListApi = (params: Series.GetSeriesListParams) => {
  return request<Series.GetSeriesListResponse>("/video/series/page", {
    method: "GET",
    params,
  });
};

/**
 * 创建剧集
 * @param params 创建剧集参数
 * @returns 创建后的剧集ID
 */
export const createSeriesApi = (params: {
  name: string;
  sourceLangCode: string;
  needRemovalSubtitle: boolean;
}) => {
  return request<{ code: number; data: number; msg: string }>("/video/series/create", {
    method: "POST",
    body: params, // 参数作为body的raw格式发送
  });
};

/**
 * 修改剧集名称
 * @param params 修改剧集名称参数
 * @returns 重命名结果
 */
export const updateSeriesNameApi = (params: {
  series_id: number;
  name: string;
}) => {
  // 新接口需要的字段为 { id, name }
  const body = {
    id: params.series_id,
    name: params.name,
  };

  return request<Series.RenameSeriesResponse>("/video/series/rename", {
    method: "PUT",
    body,
  });
};


/**
 * 修改剧集截止时间
 * @param params 修改剧集截止时间参数
 * @returns 修改后的剧集信息
 */
export const updateSeriesEndDateApi = (params: {
  series_id: number;
  end_time: string;
}) => {
  return request<Series.SeriesItem>("/series/update", {
    method: "PUT",
    body: params, // 参数作为body的raw格式发送
  });
};

/**
 * 获取剧集管理列表
 * @param params 分页参数
 * @returns 剧集管理列表数据
 */
export const getSeriesManageListApi = (params: Series.GetSeriesManageListParams) => {
  return request<Series.GetSeriesManageListResponse>("/series/manage-list", {
    method: "GET",
    params, // 参数作为查询参数，会自动添加到URL中
  });
};

/**
 * 将剧集分配给部门（团队）
 * 后端接口：/admin-api/video/series-assignment/assign-to-dept
 * 请求体参数：{ seriesId, deptId, endTime? }（endTime 可选，用于设置截止时间；deptId 可为 null，用于重置分配状态）
 * 响应数据：{ code: 0, data: 1, msg: "" }
 */
export const assignToGroupApi = (seriesId: number, deptId: number | null, endTime?: string) => {
  const body: { seriesId: number; deptId: number | null; endTime?: string } = {
    seriesId,
    deptId: deptId ?? null, // 支持传 null 重置分配状态
  };
  
  if (endTime) {
    body.endTime = endTime;
  }
  
  return request<{
    code: number;
    data: number;
    msg: string;
  }>("/video/series-assignment/assign-to-dept", {
    method: "POST",
    body,
  });
};

/**
 * 获取单个剧集详情
 * 后端接口：/admin-api/video/series/get
 * 查询参数：id
 */
export const getSeriesDetailApi = (id: number) => {
  return request<Series.GetSeriesDetailResponse>("/video/series/get", {
    method: "GET",
    params: { id },
  });
};

/**
 * 设置剧集截止时间
 * @param id 剧集语种ID
 * @param deadline 截止时间（格式：YYYY-MM-DD HH:mm:ss）
 * @returns Promise<{ code: number; data: boolean; msg: string }> 返回设置结果
 */
export const setSeriesDeadlineApi = (id: number, deadline: string) => {
  return request<{ code: number; data: boolean; msg: string }>("/video/series-assignment/set-deadline", {
    method: "PUT",
    body: {
      id,
      deadline,
    },
  });
};

/**
 * 审核员将剧集分配给操作员
 * @param id 剧集语种ID
 * @param operatorId 操作员用户ID
 * @returns 分配结果对象
 */
export const assignToOperatorApi = (id: number, operatorId: number) => {
  return request<{ code: number; data: string; msg: string }>(`/video/series-assignment/assign-to-operator`, {
    method: "PUT",
    body: {
      id,
      operatorId,
    },
  });
};

/**
 * 获取剧集内层语言列表（多语种版本列表）
 * 后端接口：/admin-api/video/series-languages/languages/page
 * 查询参数：pageNo, pageSize, seriesId（可选）, langName（可选）, sort（可选）
 */
export const getSeriesLanguagesPageApi = (params: Series.GetSeriesLanguagesPageParams) => {
  return request<Series.GetSeriesLanguagesPageResponse>("/video/series-languages/languages/page", {
    method: "GET",
    params,
  });
};

/**
 * 删除剧集语种
 * 后端接口：/admin-api/video/series-languages/languages
 * 请求参数：id（通过params传递）
 * 响应数据：{ code: 0, data: true, msg: "" }
 */
export const deleteSeriesLanguageApi = (id: number) => {
  return request<{ code: number; data: boolean; msg: string }>("/video/series-languages/languages", {
    method: "DELETE",
    params: { id },
  });
};

/**
 * 批量新增剧集语种
 * 后端接口：/admin-api/video/series-languages/add-languages-batch
 * 请求体参数：{ seriesId: number, langCodes: string[], taskIds: number[], transTerms?: TransTerm[], aiVoiceover?: boolean }
 * 响应数据：{ code: 0, data: 1, msg: "" }
 */
export const addSeriesLanguagesBatchApi = (body: { 
  seriesId: number; 
  langCodes: string[];
  taskIds: number[];
  transTerms?: Array<{ langCode: string; terminology: string }>;
  aiVoiceover?: boolean;
}) => {
  return request<{ code: number; data: number; msg: string }>("/video/series-languages/add-languages-batch", {
    method: "POST",
    body,
  });
};

/**
 * 获取剧集带语言列表（树形结构，用于剧集管理）
 * 后端接口：/admin-api/video/series/with-languages/page
 * 查询参数：
 * - ownerType（默认 1）
 * - sort（1最新分配 2最早分配 3最新结束 4最早结束；默认传 1）
 * - status（不传=全部状态；0未提交 1审核通过 3已提交；默认不传）
 * - name（剧集名称）
 */
export const getSeriesWithLanguagesPageApi = (params: Series.GetSeriesWithLanguagesPageParams = {}) => {
  // 设置默认值
  const requestParams: Series.GetSeriesWithLanguagesPageParams = {
    ownerType: 1, // 默认值为1
    sort: 1,
    ...params,
  };
  
  return request<Series.GetSeriesWithLanguagesPageResponse>("/video/series/with-languages/page", {
    method: "GET",
    params: requestParams,
  });
};

/**
 * 获取纯净版视频列表
 * 后端接口：/admin-api/video/task/pure-list
 * 查询参数：seriesId
 */
export const getPureVideoListApi = (seriesId: number) => {
  return request<{
    code: number;
    data: Array<{
      id: number;
      name: string;
      status: number;
      coverUrl: string;
      tag: number;
      fileUrl: string;
      pureVideoUrl: string;
      resultUrl: string;
      duration: number;
      createTime: string;
      updateTime: string;
    }>;
    msg: string;
  }>("/video/task/pure-list", {
    method: "GET",
    params: { seriesId },
  });
};

